import React from 'react';

/**
 * Pieces the Setup preview and the Live screen share. Rows are the bridge's
 * presented rows — the same objects the tower stream carries — so what an
 * operator sees here is what the graphics are given.
 */

const FLAG_STYLES = {
  green: 'bg-green-900/30 text-green-300 border-green-700',
  yellow: 'bg-yellow-900/30 text-yellow-300 border-yellow-700',
  fcy: 'bg-yellow-900/30 text-yellow-300 border-yellow-700',
  sc: 'bg-yellow-900/30 text-yellow-300 border-yellow-700',
  red: 'bg-red-900/40 text-red-300 border-red-700',
  finish: 'bg-[#1a1a1a] text-white border-[#3a3a3a]',
  stop: 'bg-red-900/40 text-red-300 border-red-700',
  none: 'bg-[#111111] text-gray-500 border-[#2a2a2a]',
};

const FLAG_LABELS = { green: 'Green', yellow: 'Yellow', fcy: 'FCY', sc: 'Safety car', red: 'Red', finish: 'Chequered', stop: 'Stopped', none: '-' };

export function FlagPill({ flag }) {
  if (!flag) return null;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${FLAG_STYLES[flag] || FLAG_STYLES.none}`}>
      {flag === 'finish' && <span className="mr-1">🏁</span>}
      {FLAG_LABELS[flag] || flag}
    </span>
  );
}

function Change({ change }) {
  if (!change) return null;
  return (
    <span className={`ml-1 text-[9px] ${change > 0 ? 'text-green-400' : 'text-red-400'}`}>
      {change > 0 ? '▲' : '▼'}{Math.abs(change)}
    </span>
  );
}

/**
 * The running order. With `onDriver` / `onBattle` it becomes the control
 * surface: click a name to feature that car, tick two to make them the battle.
 */
export function RunningOrder({ rows = [], compact = false, driver = null, battle = null, onDriver = null, onBattle = null, busy = false }) {
  const inBattle = (no) => Array.isArray(battle) && battle.includes(no);
  const cell = compact ? 'px-2 py-1' : 'px-3 py-1.5';

  return (
    <div className="border border-[#2a2a2a] rounded overflow-hidden overflow-x-auto">
      <table className="w-full text-xs min-w-[560px]">
        <thead className="bg-[#1a1a1a] text-gray-500">
          <tr>
            {onBattle && <th className={`${cell} text-left font-medium w-8`} title="Tick two cars for the battle stream">Bat</th>}
            <th className={`${cell} text-left font-medium w-12`}>Pos</th>
            <th className={`${cell} text-left font-medium w-12`}>No</th>
            <th className={`${cell} text-left font-medium`}>Name</th>
            <th className={`${cell} text-left font-medium`}>Class</th>
            <th className={`${cell} text-right font-medium`}>Laps</th>
            <th className={`${cell} text-right font-medium`}>Gap</th>
            <th className={`${cell} text-right font-medium`}>Int</th>
            <th className={`${cell} text-right font-medium`}>Last</th>
            <th className={`${cell} text-right font-medium`}>Best</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={`${row.no}-${i}`}
              className={`border-t border-[#2a2a2a] ${row.no === driver ? 'bg-blue-950/40' : inBattle(row.no) ? 'bg-purple-950/30' : ''}`}
            >
              {onBattle && (
                <td className={cell}>
                  <input type="checkbox" checked={inBattle(row.no)} disabled={busy} onChange={() => onBattle(row.no)} />
                </td>
              )}
              <td className={`${cell} text-white tabular-nums`}>
                {row.pos ?? '-'}
                <Change change={row.change} />
              </td>
              <td className={`${cell} tabular-nums`}>
                <span
                  className="inline-block min-w-[2rem] text-center px-1 rounded-sm font-semibold"
                  style={row.clsBg ? { background: row.clsBg, color: row.clsFg || '#000' } : undefined}
                >
                  {row.no}
                </span>
              </td>
              <td className={`${cell} text-gray-200`}>
                {onDriver
                  ? <button onClick={() => onDriver(row.no)} disabled={busy} className="hover:text-blue-400 text-left" title="Feature this car in the driver stream">{row.name}</button>
                  : row.name}
                {row.pit && <span className="ml-1.5 text-[9px] px-1 rounded bg-amber-900/40 text-amber-400">PIT</span>}
                {row.status && row.status !== 'pit' && <span className="ml-1.5 text-[9px] text-gray-500 uppercase">{row.status}</span>}
              </td>
              <td className={`${cell} text-gray-500`}>{row.cls}</td>
              <td className={`${cell} text-right text-gray-400 tabular-nums`}>{row.laps ?? ''}</td>
              <td className={`${cell} text-right text-gray-300 tabular-nums`}>{row.gap}</td>
              <td className={`${cell} text-right text-gray-400 tabular-nums`}>{row.interval}</td>
              <td className={`${cell} text-right text-gray-400 tabular-nums`}>{row.lastLap}</td>
              <td className={`${cell} text-right tabular-nums ${row.fastest ? 'text-purple-400 font-semibold' : 'text-gray-400'}`}>{row.bestLap}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
